"use client"

import { useState } from "react"
import { AnimatePresence } from "framer-motion"
import { QrCode, Smartphone, Wifi, History, ExternalLink, Phone } from "lucide-react"
import { useApp } from "@/lib/context"
import OrderHistoryModal from "./order-history-modal"
import SyncmenuPromoModal from "./syncmenu-promo-modal"

const SITE_URL = "https://www.syncmenu.ao"
const PHONE_DISPLAY = "+244 946 808 054"
const PHONE_TEL = "tel:+244946808054"

// Passos são uma sequência real: a ordem importa para quem nunca escaneou.
const SCAN_STEPS = [
  { icon: Smartphone, label: "Abra a câmera do seu celular" },
  { icon: QrCode, label: "Aponte para o código QR da mesa" },
  { icon: Wifi, label: "Acesse automaticamente o menu" },
]

export default function QrCodeMessage() {
  const { isValidSession, isSessionExpired, orderHistory } = useApp()
  const [showHistory, setShowHistory] = useState(false)
  const [showPromo, setShowPromo] = useState(false)

  // Só mostrar se não há sessão válida ou se a sessão expirou
  if (isValidSession && !isSessionExpired) {
    return null
  }

  const hasOrderHistory = orderHistory.length > 0

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-hotel-orange via-orange-500 to-orange-600 flex items-center justify-center p-4">
        <div className="relative z-10 max-w-md w-full">
          <div className="bg-background/95 backdrop-blur-xl rounded-lg shadow-xl border border-border divide-y divide-border">
            {/* ===== 1. Estado da sessão ===== */}
            <div className="px-6 pt-7 pb-6 text-center">
              <div className="w-14 h-14 bg-primary rounded-lg flex items-center justify-center mx-auto mb-5">
                <QrCode className="w-7 h-7 text-primary-foreground" />
              </div>

              <h1 className="text-xl font-bold text-foreground mb-2">
                {isSessionExpired ? "Sessão Expirada" : "Acesso Necessário"}
              </h1>

              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
                {isSessionExpired
                  ? "Sua sessão expirou por segurança. Escaneie o código QR novamente para continuar fazendo pedidos."
                  : "Para acessar nosso menu digital e fazer pedidos, escaneie o código QR disponível na sua mesa."}
              </p>
            </div>

            {/* ===== 2. Como escanear ===== */}
            <div className="px-6 py-5">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                Como acessar
              </p>
              <ol className="space-y-3">
                {SCAN_STEPS.map((step, index) => (
                  <li key={step.label} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-md bg-muted text-foreground text-xs font-semibold grid place-items-center shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-sm text-foreground">{step.label}</span>
                    <step.icon className="w-4 h-4 text-muted-foreground ml-auto shrink-0" />
                  </li>
                ))}
              </ol>

              {hasOrderHistory && (
                <button
                  onClick={() => setShowHistory(true)}
                  className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground text-sm font-medium rounded-md transition-colors"
                >
                  <History className="w-4 h-4" />
                  Ver pedidos anteriores
                  <span className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                    {orderHistory.length}
                  </span>
                </button>
              )}
            </div>

            {/* ===== 3. Publicidade Syncmenu ===== */}
            <div className="px-6 py-5">
              <div className="flex flex-col items-center text-center gap-2">
                <img
                  src="/syncmenu-logo.svg"
                  alt="Syncmenu"
                  className="h-10 w-auto rounded-md border border-border bg-white"
                />
                <p className="text-sm font-semibold text-foreground leading-snug">
                  7 dias grátis para o seu estabelecimento
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Menu digital, faturação, estoque e muito mais. Planos para restaurantes e hotéis —
                  desde 10.000 Kz/mês após o teste.
                </p>
              </div>

              <div className="flex gap-2 mt-3">
                <a
                  href={SITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 bg-hotel-gold hover:bg-hotel-gold-dark hover:text-white text-hotel-dark text-[11px] font-bold rounded-md transition-colors"
                >
                  www.syncmenu.ao
                  <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  onClick={() => setShowPromo(true)}
                  className="flex-1 inline-flex items-center justify-center px-2.5 py-1.5 border border-border hover:bg-muted text-foreground text-[11px] font-medium rounded-md transition-colors"
                >
                  Saber mais
                </button>
              </div>

              <div className="flex justify-center mt-2.5">
                <a
                  href={PHONE_TEL}
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  <Phone className="w-3 h-3" />
                  Ligar agora: {PHONE_DISPLAY}
                </a>
              </div>
            </div>

            {/* ===== 4. Rodapé ===== */}
            <div className="py-3.5 text-center">
              <p className="text-xs text-muted-foreground">
                Powered by <span className="font-semibold text-primary">Syncmenu.ao</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modais */}
      <OrderHistoryModal isOpen={showHistory} onClose={() => setShowHistory(false)} />

      <AnimatePresence>
        {showPromo && <SyncmenuPromoModal isOpen={showPromo} onClose={() => setShowPromo(false)} />}
      </AnimatePresence>
    </>
  )
}
